// Trends page (CRO Zone, Phase 3). Filters (time window · call-type group · team/region/manager slice)
// → per-skill graphs (avg vs the snapshotted bar) + a ≤7-column heatmap. Scores-only. The critical-
// trends SUMMARY headline is Phase 4; this ships the graphs + heatmap. Fails quiet on error.

import * as api from '../lib/api.js';
import { skillLabelById, SKILL_ORDER } from '../lib/skills.js';
import { renderHeatmap } from './heatmap.js';
import { navigateTo } from './sidenav.js';

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}
function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>): SVGElementTagNameMap[K] {
  const n = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
}

const WINDOWS: { token: string; label: string }[] = [
  { token: '7d', label: '7 days' },
  { token: '30d', label: '30 days' },
  { token: '90d', label: '90 days' },
  { token: '6m', label: '6 months' },
  { token: '12m', label: '12 months' },
];

// Uniform WEEKLY heatmap columns across every window: each window reads the same way, the latest week
// sits on the right, and only the lookback changes. Empty weeks are dropped server-side; this just
// budgets how many populated weeks fit the current width so the heatmap leverages the screen.
const SKILL_COL_PX = 170, CELL_PX = 60, MIN_COLS = 4, MAX_COLS = 26; // MAX matches the server backstop
function fitCols(availWidth: number): number {
  return Math.max(MIN_COLS, Math.min(MAX_COLS, Math.floor((availWidth - SKILL_COL_PX) / CELL_PX)));
}
function heatWidth(root: HTMLElement): number {
  return root.clientWidth || window.innerWidth - 300; // fall back to viewport minus the nav when unlaid-out
}

// Friendly labels for the scorecard-group keys (scorecardKeyForCallType Columns).
const GROUP_LABEL: Record<string, string> = {
  Cold: 'Cold / Prospecting',
  Inbound: 'Inbound',
  ProductDemo: 'First buyer-seller engagement calls',
  TechVal: 'Technical Validation',
  Follow: 'Follow-up',
  Onboard: 'Onboarding',
  QBR: 'QBR / Renewal',
  Commercial: 'Commercial / Negotiation',
  Adoption: 'Adoption / Health',
  Expansion: 'Expansion',
  Escalation: 'Escalation',
  Default: 'Other / Unidentified',
};
export const groupLabel = (k: string): string => GROUP_LABEL[k] ?? k;

// Optional explainer for a call-type group, shown as an inline (i) beside the label. Keyed by group key.
// One tip per scorecard group so both Trends and Benchmark (which share groupTip) explain what each
// call-type bucket spans and what its scores reward.
const GROUP_TIP: Record<string, readonly string[]> = {
  Cold: [
    'Cold outbound and prospecting calls, where the rep reaches a buyer who has not engaged yet.',
    'Scored against a cold-call scorecard, so opening, relevance, and earning the next step matter most.',
  ],
  Inbound: [
    'Inbound first calls, where the buyer reached out and the rep qualifies the fit.',
    'Scored on qualification and routing to the right next step, not on prospecting.',
  ],
  ProductDemo: [
    'The first calls where buyer and seller engage: Introduction, Discovery, Follow-ups, Product Demo, and Business Case.',
    'They are grouped together because they share the same early-stage job — uncovering the problem and proving fit — so scores compare fairly.',
  ],
  TechVal: [
    'Technical validation calls: POC, POV, security, and compliance reviews.',
    'Scored on proving the solution works and clearing technical risk.',
  ],
  Follow: [
    'Mid-cycle follow-up and progression calls between the major milestones.',
    'Scored on moving the deal forward and keeping momentum.',
  ],
  Onboard: [
    'Onboarding, kickoff, and implementation calls after the deal closes.',
    'Scored on setting the customer up for a successful start.',
  ],
  QBR: [
    'Business reviews, QBRs, EBRs, and renewal calls.',
    'Scored on demonstrating the value delivered and securing the renewal.',
  ],
  Commercial: [
    'Commercial calls: negotiation, pricing, procurement, and legal review.',
    'Scored on protecting value and closing the terms.',
  ],
  Adoption: [
    'Adoption and health-check calls, including customer training.',
    'Scored on driving usage and heading off churn risk.',
  ],
  Expansion: [
    'Expansion calls: upsell and cross-sell into an existing account.',
    'Scored on uncovering and developing new opportunity.',
  ],
  Escalation: [
    'Escalation calls, both support and business, when something has gone wrong.',
    'Scored on resolving the issue and restoring trust.',
  ],
  Default: [
    'Calls whose type could not be identified from the transcript.',
    'They are grouped here so their scores are not mixed into a specific call type.',
  ],
};
export const groupTip = (k: string): readonly string[] | undefined => GROUP_TIP[k];

// --- state ---
let filters: api.TrendsFilters | null = null;
const sel: api.TrendsSelection = { window: '90d' };
let resizeWired = false;
// The active page context. Trends (per-skill charts) and Heatmap are two pages that SHARE `sel`/`filters`
// and this ctx, each with its own left card body (#…-page) + right filter rail (#…-rail). A filter change
// on either re-paints whichever page is on screen. `mode` picks what the left card renders.
let ctx: { pageRoot: HTMLElement; railRoot: HTMLElement; mode: 'trends' | 'heatmap' } | null = null;

// Re-fetch at a new granularity when the viewport crosses a column threshold (debounced; only while the
// Trends view is on screen and only when the chosen granularity actually changes).
function wireResize(): void {
  if (resizeWired) return;
  resizeWired = true;
  let t: number | undefined;
  window.addEventListener('resize', () => {
    window.clearTimeout(t);
    t = window.setTimeout(() => {
      if (!ctx || ctx.pageRoot.offsetParent === null) return; // page not visible → skip
      if (fitCols(heatWidth(ctx.pageRoot)) !== sel.heatCols) reload(); // width crossed a column threshold → refetch
    }, 250);
  });
}

/** The Trends page: per-skill trend charts (left card) + the shared filter rail (right). */
export function renderTrendsPage(): void { mount('trends-page', 'trends-rail', 'trends'); }
/** The Heatmap page: the skill×week heatmap (left card) + the SAME shared filter rail (right). */
export function renderHeatmapPage(): void { mount('heatmap-page', 'heatmap-rail', 'heatmap'); }

function mount(pageId: string, railId: string, mode: 'trends' | 'heatmap'): void {
  const pageRoot = document.getElementById(pageId);
  const railRoot = document.getElementById(railId);
  if (!pageRoot || !railRoot) return;
  ctx = { pageRoot, railRoot, mode };
  wireResize();
  pageRoot.textContent = '';
  pageRoot.appendChild(h('p', 'tm-loading', 'Loading trends…'));
  if (filters && !filters.trendsOff) { reload(); return; } // options already fetched this session → straight to data
  api
    .getTrendsFilters()
    .then((f) => {
      filters = f;
      if (f.trendsOff) {
        railRoot.textContent = '';
        pageRoot.textContent = '';
        pageRoot.appendChild(h('p', 'tt-empty', 'Trends are turned off for this account. Turn them on in Account to build the picture.'));
        return;
      }
      if (!sel.group && f.groups.length) sel.group = f.groups[0]!.key; // default to the busiest group
      reload();
    })
    .catch(() => {
      pageRoot.textContent = '';
      pageRoot.appendChild(h('p', 'tt-empty', 'Trends are unavailable right now. Please try again.'));
    });
}

/** Fetch the overview for the current selection and paint the active page (rail + body). */
function reload(): void {
  if (!ctx) return;
  const { pageRoot } = ctx;
  sel.granularity = 'week'; // uniform weekly columns across all windows (user choice)
  sel.heatCols = fitCols(heatWidth(pageRoot)); // width-derived budget of populated weeks
  api
    .getTrendsOverview(sel)
    .then((o) => paint(o))
    .catch(() => {
      pageRoot.textContent = '';
      pageRoot.appendChild(h('p', 'tt-empty', 'Could not load these trends. Try a different filter.'));
    });
}

function paint(o: api.TrendsOverview): void {
  if (!ctx) return;
  const { pageRoot, railRoot, mode } = ctx;
  if (o.group) sel.group = o.group; // keep the Call type dropdown in sync with the group the server resolved
  // right rail = the shared filter cards
  railRoot.textContent = '';
  railRoot.appendChild(filterRail(o));
  // left card body
  pageRoot.textContent = '';
  if (o.trendsOff) { pageRoot.appendChild(h('p', 'tt-empty', 'Trends are turned off for this account.')); return; }
  if (o.error || !o.meta || o.meta.totalCalls === 0) { pageRoot.appendChild(emptyState()); return; }
  if (mode === 'heatmap') {
    pageRoot.appendChild(sectionTitle('Heatmap', HEATMAP_TIP, HEATMAP_NOTE));
    pageRoot.appendChild(heatmap(o));
    return;
  }
  pageRoot.appendChild(summaryBlock(o));
  pageRoot.appendChild(sectionTitle('Trend by skill'));
  pageRoot.appendChild(graphGrid(o));
}

// --- filter rail (the right-hand column on BOTH the Trends and Heatmap pages) ---
// Two stacked HUD cards: WINDOW (segmented presets + Custom range) and FILTERS (the six dropdowns,
// preceded by the coverage line). Reads/writes the shared `sel`; every control ends with reload().
function filterRail(o: api.TrendsOverview): HTMLElement {
  const rail = h('div', 'trf-rail-inner');

  // Window card
  const winCard = h('div', 'tr-rail-card');
  winCard.appendChild(h('div', 'tr-rail-h', 'Window'));
  const seg = h('div', 'trf-seg');
  for (const w of WINDOWS) {
    const b = h('button', 'trf-seg-btn' + (sel.window === w.token ? ' is-on' : ''), w.label);
    b.type = 'button';
    b.addEventListener('click', () => { sel.window = w.token; reload(); });
    seg.appendChild(b);
  }
  winCard.appendChild(seg);
  winCard.appendChild(customRow()); // "Custom" toggle + start/end date pickers, below the presets
  rail.appendChild(winCard);

  // Filters card
  const fCard = h('div', 'tr-rail-card');
  fCard.appendChild(h('div', 'tr-rail-h', 'Filters'));
  if (o.meta && o.meta.callsWithBar > 0) fCard.appendChild(coverageNote(o));
  fCard.appendChild(dropdown( // Call type first — it's the primary axis (one call type at a time); gold-highlighted
    'Call type',
    (filters?.groups ?? []).map((g) => ({ value: g.key, label: `${groupLabel(g.key)} (${g.count})` })),
    sel.group ?? '',
    false,
    (v) => { sel.group = v; reload(); },
    true,
  ));
  fCard.appendChild(sliceDropdown('Team', 'team', filters?.teams ?? []));
  fCard.appendChild(sliceDropdown('Class', 'class', filters?.classes ?? []));
  fCard.appendChild(sliceDropdown('Region', 'region', filters?.regions ?? []));
  fCard.appendChild(repDropdown(true)); // near the bottom of the rail → open the panel upward
  fCard.appendChild(sliceDropdown('Manager', 'manager', filters?.managers ?? [], true));
  rail.appendChild(fCard);

  return rail;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const ymd = (ms: number): string => new Date(ms).toISOString().slice(0, 10); // local-agnostic YYYY-MM-DD (UTC)

/** The "Custom" row under the window presets: a toggle that, when on, reveals a start/end date pair.
 *  Picking Custom seeds a sensible default (last 90 days) so the view has data immediately; editing a
 *  date reloads only once both ends are set and ordered. Choosing any preset above turns Custom off. */
function customRow(): HTMLElement {
  const wrap = h('div', 'trf-custom');
  const active = sel.window === 'custom';
  const btn = h('button', 'trf-custom-btn' + (active ? ' is-on' : ''), 'Custom');
  btn.type = 'button';
  btn.addEventListener('click', () => {
    if (sel.window === 'custom') return; // already in custom mode — leave the dates as they are
    sel.window = 'custom';
    if (!sel.from) sel.from = ymd(Date.now() - 90 * DAY_MS);
    if (!sel.to) sel.to = ymd(Date.now());
    reload();
  });
  wrap.appendChild(btn);

  if (active) {
    wrap.appendChild(dateInput(sel.from ?? '', (v) => { sel.from = v; maybeReloadCustom(); }));
    wrap.appendChild(h('span', 'trf-sep', '→'));
    wrap.appendChild(dateInput(sel.to ?? '', (v) => { sel.to = v; maybeReloadCustom(); }));
  }
  return wrap;
}

function dateInput(value: string, onChange: (v: string) => void): HTMLInputElement {
  const i = h('input', 'trf-date');
  i.type = 'date';
  i.value = value;
  i.addEventListener('change', () => onChange(i.value));
  return i;
}

/** Reload only when both custom ends are set and in order — otherwise wait for the second date so we
 *  don't fire an empty/backwards range mid-edit. */
function maybeReloadCustom(): void {
  if (sel.from && sel.to && sel.from <= sel.to) reload();
}

/** The "Rep's name" filter: empty = all reps; picking a rep filters the whole page (trends, gaps, and
 *  what-good-looks-like) to that rep via the overview's rep_key filter. */
function repDropdown(up = false): HTMLElement {
  const opts = [
    { value: '', label: 'All reps' },
    ...(filters?.reps ?? []).map((r) => ({ value: r.key, label: `${r.name} (${r.count})` })),
  ];
  return dropdown("Rep's name", opts, sel.rep ?? '', true, (v) => {
    if (v) sel.rep = v;
    else delete sel.rep;
    delete sel.group; // re-default the call type to the (new) selection's busiest
    reload();
  }, false, up);
}

/** Open the Trends page pre-filtered to one rep (used by the Average Scores page's clickable names).
 *  Clears the org-entity slices so a stale slice can't hide the rep; the group re-defaults on render. */
export function openTrendsForRep(repKey: string): void {
  if (repKey) sel.rep = repKey;
  else delete sel.rep;
  delete sel.team;
  delete sel.region;
  delete sel.manager;
  delete sel.class;
  delete sel.group;
  navigateTo('trends');
}

function sliceDropdown(label: string, key: 'team' | 'region' | 'manager' | 'class', items: { id: string; name: string; rep_count: number }[], up = false): HTMLElement {
  const opts = [
    { value: '__all__', label: 'All' },
    { value: '', label: 'Unassigned' }, // '' → the IS NULL bucket, never a silent drop
    ...items.map((i) => ({ value: i.id, label: `${i.name} (${i.rep_count})` })),
  ];
  const current = sel[key] === undefined ? '__all__' : sel[key] === null ? '__all__' : sel[key]!;
  return dropdown(label, opts, current, true, (v) => {
    if (v === '__all__') delete sel[key];
    else sel[key] = v; // '' stays '' → Unassigned
    reload();
  }, false, up);
}

// --- custom dark dropdown ---------------------------------------------------------------------------
// Native <select> popups are OS-rendered and can't be reliably dark-styled (esp. macOS), so the rail
// uses a lightweight custom dropdown: a button + an absolutely-positioned dark panel of options. The
// `gold` variant paints the current value + selected option gold (the active Call type).
let openCS: HTMLElement | null = null;
let csWired = false;
function closeCS(): void {
  if (!openCS) return;
  openCS.classList.remove('is-open');
  const p = openCS.querySelector<HTMLElement>('.trf-cs-panel');
  if (p) p.hidden = true;
  openCS = null;
}
function wireCS(): void {
  if (csWired) return;
  csWired = true;
  document.addEventListener('click', () => closeCS()); // any outside click closes the open panel
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeCS(); });
}

function dropdown(label: string, opts: { value: string; label: string }[], current: string, allowEmptyValue: boolean, onChange: (v: string) => void, gold = false, up = false): HTMLElement {
  wireCS();
  const g = h('div', 'trf-group');
  g.appendChild(h('span', 'trf-label', label));
  const cs = h('div', 'trf-cs' + (gold ? ' trf-cs-gold' : '') + (up ? ' trf-cs-up' : '') + (opts.length ? '' : ' trf-cs-disabled'));
  cs.tabIndex = opts.length ? 0 : -1;
  cs.setAttribute('role', 'button');
  cs.setAttribute('aria-haspopup', 'listbox');
  cs.setAttribute('aria-label', label);
  cs.appendChild(h('span', 'trf-cs-val', opts.find((o) => o.value === current)?.label ?? opts[0]?.label ?? '—'));
  cs.appendChild(h('span', 'trf-cs-caret', '▾'));
  const panel = h('div', 'trf-cs-panel');
  panel.setAttribute('role', 'listbox');
  panel.hidden = true;
  for (const o of opts) {
    const opt = h('div', 'trf-cs-opt' + (o.value === current ? ' is-sel' : ''), o.label);
    opt.setAttribute('role', 'option');
    opt.addEventListener('click', (e) => { e.stopPropagation(); closeCS(); onChange(o.value); });
    panel.appendChild(opt);
  }
  cs.appendChild(panel);
  const toggle = (e: Event): void => {
    e.stopPropagation();
    const wasOpen = cs.classList.contains('is-open');
    closeCS();
    if (!wasOpen && opts.length) { cs.classList.add('is-open'); panel.hidden = false; openCS = cs; }
  };
  cs.addEventListener('click', toggle);
  cs.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(e); } });
  void allowEmptyValue;
  g.appendChild(cs);
  return g;
}

// --- coverage / honesty note ---
// The excluded clause only ever appears for accounts carrying calls scored before a Scorecard minimum
// was recorded (legacy data). A fresh account records a minimum from its first scored call — using the
// built-in defaults or its own edits — so callsWithBar === totalCalls and the whole clause is absent.
const MIN_TIP: readonly string[] = [
  'The minimum level for each skill comes from your Scorecard — the built-in defaults or your own edits on the Scorecards page.',
  'These calls were scored before a Scorecard minimum was recorded for them, so they can’t be shown above or below the minimum and aren’t charted.',
  'Grading is automatic from your first call — new accounts never see this. More in Getting Started › The CRO Zone.',
];

// Header (i) for the heatmap — what a cell is and how it's coloured.
const HEATMAP_TIP: readonly string[] = [
  'Each cell is the average score for that skill across the calls in that week.',
  'Colour compares the average to the Scorecard minimum — green at or above, beige just below, red further below.',
  'Columns are weekly across every window; weeks with no calls are hidden, so only weeks with activity show, most recent on the right.',
];
const HEATMAP_NOTE = 'Weeks with no calls are hidden — columns stay packed.';

/** Small inline (i) that reveals `lines` on hover/focus. Reuses the shared .meta-info / .sc-note-tip
 *  styling (the tip is pointer-events:none, so `lines` are plain text — no links inside). */
function infoIcon(lines: readonly string[], label: string): HTMLElement {
  const wrap = h('span', 'meta-info');
  wrap.tabIndex = 0;
  wrap.setAttribute('role', 'note');
  wrap.setAttribute('aria-label', label);
  const ico = h('span', 'meta-info-ico');
  ico.setAttribute('aria-hidden', 'true');
  ico.textContent = 'i';
  const tip = h('span', 'sc-note-tip');
  tip.setAttribute('role', 'tooltip');
  lines.forEach((line, i) => {
    if (i) tip.appendChild(document.createElement('br'));
    tip.appendChild(document.createTextNode(line));
  });
  wrap.append(ico, tip);
  return wrap;
}

function coverageNote(o: api.TrendsOverview): HTMLElement {
  const n = h('p', 'trends-coverage');
  const since = new Date(o.meta.fromMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const shown = o.meta.callsWithBar; // charted calls (those with a recorded Scorecard minimum)
  const excluded = o.meta.totalCalls - o.meta.callsWithBar;
  n.appendChild(document.createTextNode(`${shown} call${shown === 1 ? '' : 's'} since ${since}`));
  if (excluded > 0) {
    n.appendChild(document.createTextNode(` · ${excluded} not shown — no Scorecard minimum recorded `));
    n.appendChild(infoIcon(MIN_TIP, 'Why some calls aren’t charted'));
  }
  return n;
}

function sectionTitle(t: string, tip?: readonly string[], note?: string): HTMLElement {
  const el = h('h3', 'trends-sec-h');
  el.appendChild(document.createTextNode(t));
  if (tip) el.appendChild(infoIcon(tip, `About the ${t}`));
  if (note) el.appendChild(h('span', 'trends-sec-note', note));
  return el;
}

// --- critical-trends summary (the headline, §4.2) ---
const CONF_LABEL: Record<api.TrendConfidence, string> = { high: 'High', medium: 'Medium', low: 'Low' };
const pct = (p: number): string => `${Math.round(p * 100)}%`;

function summaryBlock(o: api.TrendsOverview): HTMLElement {
  const s = o.summary;
  const g = groupLabel(o.group);
  const wrap = h('div', 'trends-summary');

  // A light header naming the selected call-type group, with an (i) explaining what it spans when a tip
  // exists (e.g. the early-stage engagement cluster).
  const head = h('div', 'trends-group-head');
  head.appendChild(h('span', 'trends-group-h', g));
  const gtip = groupTip(o.group);
  if (gtip) head.appendChild(infoIcon(gtip, `What ${g} covers`));
  wrap.appendChild(head);

  const nothing = !s.gaps.length && !s.strengths.length && s.finalDirection.direction === 'flat' && !s.improved.length && !s.declined.length;
  if (nothing) {
    wrap.appendChild(h('p', 'trends-summary-none', `Not enough of a pattern yet to call out for ${g} calls. Trends need at least ${5} scored calls per skill to state a claim — keep scoring and the headlines appear here.`));
    return wrap;
  }

  // 1) General insights — overall movement + the biggest movers — sit above the two blocks.
  const insights = h('div', 'trends-insights');
  let hasInsight = false;
  if (s.finalDirection.direction !== 'flat' && s.finalDirection.from != null && s.finalDirection.to != null) {
    const dir = s.finalDirection.direction === 'down' ? 'declining' : 'improving';
    insights.appendChild(h('p', 'trends-headline', `${g} calls show a ${dir} average final score over this window (${Math.round(s.finalDirection.from)} → ${Math.round(s.finalDirection.to)}).`));
    hasInsight = true;
  }
  if (s.improved.length) { insights.appendChild(moverLine('Most improved', s.improved)); hasInsight = true; }
  if (s.declined.length) { insights.appendChild(moverLine('Most declined', s.declined)); hasInsight = true; }
  if (hasInsight) wrap.appendChild(insights);

  // 2) Two separate blocks: critical gaps, then what good looks like.
  const cols = h('div', 'trends-summary-cols');
  cols.appendChild(statCard('Critical gaps', s.gaps, g, 'gap'));
  cols.appendChild(statCard('What good looks like', s.strengths, g, 'strength'));
  wrap.appendChild(cols);
  return wrap;
}

function statCard(title: string, stats: api.TrendSummaryStat[], groupName: string, kind: 'gap' | 'strength'): HTMLElement {
  const card = h('div', 'trends-scard');
  card.appendChild(h('h4', 'trends-scol-h', title));
  if (!stats.length) {
    card.appendChild(h('p', 'trends-scol-empty', kind === 'gap' ? 'No skill is consistently below the minimum here.' : 'No skill yet clears the minimum consistently enough to call a strength.'));
    return card;
  }
  const list = h('ul', 'trends-stat-list');
  for (const st of stats) {
    const li = h('li', 'trends-stat');
    if (st.critical) li.appendChild(h('span', 'trends-crit-badge', 'critical'));
    const verb = kind === 'gap' ? 'below the minimum' : 'met or beat the minimum';
    li.appendChild(h('strong', undefined, `${skillLabelById(st.skill_id)} — ${pct(st.pct)}`));
    li.appendChild(document.createTextNode(` of ${groupName} calls ${verb}`));
    li.appendChild(h('span', 'trends-stat-meta', ` (n=${st.n} · ${CONF_LABEL[st.confidence]} confidence)`));
    list.appendChild(li);
  }
  card.appendChild(list);
  return card;
}

function moverLine(title: string, movers: api.TrendMover[]): HTMLElement {
  const p = h('p', 'trends-mover');
  p.appendChild(h('span', 'trends-mover-h', title + ': '));
  p.appendChild(
    document.createTextNode(
      movers.map((m) => `${skillLabelById(m.skill_id)} ${m.delta > 0 ? '+' : ''}${m.delta} (${m.from.toFixed(1)}→${m.to.toFixed(1)})`).join(', '),
    ),
  );
  return p;
}

// --- per-skill graphs (small multiples) ---
function graphGrid(o: api.TrendsOverview): HTMLElement {
  const grid = h('div', 'trends-graph-grid');
  // Only skills with data in-window get a card — and that same list drives the modal's prev/next.
  const displayed = o.skills.filter((s) => s.points.some((p) => p.avg != null));
  displayed.forEach((s, i) => grid.appendChild(skillCard(o, displayed, i)));
  if (!displayed.length) grid.appendChild(h('p', 'tt-empty', 'No per-skill data in this window.'));
  return grid;
}

/** Representative minimum line for a series: the most recent non-null bar (matches the sparkline). */
function seriesBar(s: api.TrendSkillSeries): number | null {
  return [...s.points].reverse().find((p) => p.bar != null)?.bar ?? null;
}

function skillCard(o: api.TrendsOverview, displayed: api.TrendSkillSeries[], index: number): HTMLElement {
  const s = displayed[index]!;
  const card = h('div', 'trends-card');
  const head = h('div', 'trends-card-head');
  head.appendChild(h('span', 'trends-card-name', skillLabelById(s.skill_id)));
  const scored = s.points.filter((p) => p.avg != null);
  const latest = scored.length ? scored[scored.length - 1]!.avg : null;
  const bar = seriesBar(s);
  const right = h('div', 'trends-card-right');
  const val = h('span', 'trends-card-val', latest != null ? latest.toFixed(1) : '—');
  if (latest != null && bar != null) val.classList.add(latest >= bar ? 'is-above' : latest >= bar - 0.5 ? 'is-near' : 'is-below');
  right.appendChild(val);
  const expand = h('button', 'trends-card-expand') as HTMLButtonElement;
  expand.type = 'button';
  expand.setAttribute('aria-label', `Expand ${skillLabelById(s.skill_id)} graph`);
  expand.title = 'Expand';
  expand.appendChild(svgEl('svg', { viewBox: '0 0 24 24', width: 15, height: 15, fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' }));
  const g = expand.firstChild as SVGElement;
  g.appendChild(svgEl('path', { d: 'M9 4H5a1 1 0 0 0-1 1v4M15 4h4a1 1 0 0 1 1 1v4M9 20H5a1 1 0 0 1-1-1v-4M15 20h4a1 1 0 0 0 1-1v-4' }));
  expand.addEventListener('click', () => openSkillModal(o, displayed, index, expand));
  right.appendChild(expand);
  head.appendChild(right);
  card.appendChild(head);
  card.appendChild(sparkline(s.points, bar));
  return card;
}

/** A mini line of avg-per-scored-bucket over a fixed 1–5 score domain, with a dotted bar reference
 *  line. The scored points are distributed evenly across the FULL width — first on the left edge,
 *  last on the right edge — so the trend always fills the card regardless of the window length or
 *  where in the window the calls fell. Empty buckets are collapsed rather than drawn as dead space
 *  (this is a compact preview; the expanded view is where a time-proportional axis belongs). */
function sparkline(points: api.TrendSkillSeries['points'], bar: number | null): SVGSVGElement {
  const W = 240, H = 64, padX = 4, padY = 6;
  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, class: 'trends-spark', width: W, height: H, role: 'img' });
  const y = (v: number): number => H - padY - ((Math.max(1, Math.min(5, v)) - 1) / 4) * (H - 2 * padY);

  if (bar != null) {
    const yb = y(bar);
    svg.appendChild(svgEl('line', { x1: padX, y1: yb, x2: W - padX, y2: yb, class: 'trends-spark-bar' }));
  }
  // Scored buckets only, in chronological order, spread edge-to-edge across the width.
  const scored = points.filter((p) => p.avg != null);
  const m = scored.length;
  const x = (k: number): number => (m <= 1 ? W / 2 : padX + (k / (m - 1)) * (W - 2 * padX));
  if (m >= 2) {
    svg.appendChild(svgEl('polyline', { points: scored.map((p, k) => `${x(k)},${y(p.avg!)}`).join(' '), class: 'trends-spark-line' }));
  }
  scored.forEach((p, k) => {
    const v = p.avg!;
    const dot = svgEl('circle', { cx: x(k), cy: y(v), r: 2.6, class: 'trends-spark-dot' + (bar != null ? (v >= bar ? ' is-above' : v >= bar - 0.5 ? ' is-near' : ' is-below') : '') });
    svg.appendChild(dot);
  });
  return svg;
}

// --- expand-to-full-size modal ---
const GRANULARITY_NOUN: Record<string, string> = { day: 'day', week: 'week', month: 'month' };

/** Open the large single-skill chart. `displayed` is the same list the grid shows, so ◂ ▸ walk between
 *  skills without leaving the modal. `opener` regains focus on close. */
function openSkillModal(o: api.TrendsOverview, displayed: api.TrendSkillSeries[], startIndex: number, opener: HTMLElement): void {
  let index = startIndex;
  const overlay = h('div', 'trends-modal-overlay');
  const dialog = h('div', 'trends-modal');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.tabIndex = -1;
  overlay.appendChild(dialog);

  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
    opener.focus();
  };
  const go = (d: number): void => {
    index = (index + d + displayed.length) % displayed.length;
    paint();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
  };

  function paint(): void {
    dialog.textContent = '';
    const s = displayed[index]!;
    const bar = seriesBar(s);
    const winLabel = WINDOWS.find((w) => w.token === (sel.window ?? '90d'))?.label ?? '';

    const head = h('div', 'trends-modal-head');
    const titles = h('div', 'trends-modal-titles');
    titles.appendChild(h('h3', 'trends-modal-title', skillLabelById(s.skill_id)));
    titles.appendChild(h('p', 'trends-modal-sub', `${groupLabel(o.group)} · last ${winLabel}`));
    head.appendChild(titles);
    const closeBtn = h('button', 'trends-modal-x');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', close);
    head.appendChild(closeBtn);
    dialog.appendChild(head);

    dialog.appendChild(fullChart(s, bar, o.buckets));

    const foot = h('div', 'trends-modal-foot');
    const noun = GRANULARITY_NOUN[o.granularity] ?? 'period';
    foot.appendChild(h('p', 'trends-modal-cap', `Each point is a ${noun} average${bar != null ? `; the dashed line is the Scorecard minimum (${bar})` : ''}. Green sits above the minimum, red below.`));
    if (displayed.length > 1) {
      const nav = h('div', 'trends-modal-nav');
      const prev = h('button', 'trends-modal-navbtn', '◂ Prev');
      prev.type = 'button';
      prev.addEventListener('click', () => go(-1));
      const next = h('button', 'trends-modal-navbtn', 'Next ▸');
      next.type = 'button';
      next.addEventListener('click', () => go(1));
      nav.append(prev, next);
      foot.appendChild(nav);
    }
    dialog.appendChild(foot);
    dialog.focus();
  }

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.addEventListener('keydown', onKey);
  document.body.appendChild(overlay);
  paint();
}

/** Full-size time chart: the window's real timeline across all buckets (gaps show as spacing), a 1–5
 *  y-axis with gridlines, a labelled minimum line, and coloured points carrying a native hover note. */
function fullChart(s: api.TrendSkillSeries, bar: number | null, buckets: api.TrendBucket[]): SVGSVGElement {
  const W = 720, H = 360, padL = 40, padR = 56, padT = 18, padB = 40;
  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, class: 'trends-fchart', role: 'img', 'aria-label': `${skillLabelById(s.skill_id)} trend` });
  const n = buckets.length;
  const x = (i: number): number => (n <= 1 ? (padL + W - padR) / 2 : padL + (i / (n - 1)) * (W - padL - padR));
  const y = (v: number): number => (H - padB) - ((Math.max(1, Math.min(5, v)) - 1) / 4) * (H - padT - padB);

  // Y gridlines + labels (1..5).
  for (let v = 1; v <= 5; v++) {
    const yy = y(v);
    svg.appendChild(svgEl('line', { x1: padL, y1: yy, x2: W - padR, y2: yy, class: 'trends-fc-grid' }));
    const lbl = svgEl('text', { x: padL - 8, y: yy + 3, class: 'trends-fc-ylbl' });
    lbl.textContent = String(v);
    svg.appendChild(lbl);
  }

  // Minimum reference line + right-edge label.
  if (bar != null) {
    const yb = y(bar);
    svg.appendChild(svgEl('line', { x1: padL, y1: yb, x2: W - padR, y2: yb, class: 'trends-fc-min' }));
    const t = svgEl('text', { x: W - padR + 6, y: yb + 3, class: 'trends-fc-minlbl' });
    t.textContent = `Min ${bar}`;
    svg.appendChild(t);
  }

  // X-axis tick labels — thinned to at most ~8 so they never crowd.
  const idx = new Map(buckets.map((b, i) => [b.key, i]));
  const step = Math.max(1, Math.ceil(n / 8));
  buckets.forEach((b, i) => {
    if (i % step !== 0 && i !== n - 1) return;
    const t = svgEl('text', { x: x(i), y: H - padB + 18, class: 'trends-fc-xlbl' });
    t.textContent = b.label;
    svg.appendChild(t);
  });

  // Series line across scored buckets at their true time positions.
  const scored = s.points.filter((p) => p.avg != null).map((p) => ({ i: idx.get(p.bucket) ?? 0, v: p.avg!, n: p.n }));
  if (scored.length >= 2) {
    svg.appendChild(svgEl('polyline', { points: scored.map((p) => `${x(p.i)},${y(p.v)}`).join(' '), class: 'trends-fc-line' }));
  }
  scored.forEach((p) => {
    const cls = bar != null ? (p.v >= bar ? ' is-above' : p.v >= bar - 0.5 ? ' is-near' : ' is-below') : '';
    const dot = svgEl('circle', { cx: x(p.i), cy: y(p.v), r: 4, class: 'trends-fc-dot' + cls });
    const title = svgEl('title', {});
    const rel = bar != null ? (p.v >= bar ? 'above minimum' : 'below minimum') : 'no minimum';
    title.textContent = `${buckets[p.i]!.label}: ${p.v.toFixed(1)} avg · ${p.n} call${p.n === 1 ? '' : 's'} · ${rel}`;
    dot.appendChild(title);
    svg.appendChild(dot);
  });
  return svg;
}

// --- heatmap (shared renderer; see ui/heatmap.ts) ---
function heatmap(o: api.TrendsOverview): HTMLElement {
  const columns = o.heatmapBuckets.map((b) => ({ label: b.label }));
  const order = new Map(SKILL_ORDER.map((s, i) => [s.id, i]));
  const rows = o.heatmap
    .filter((row) => row.cells.some((c) => c.n > 0)) // hide all-empty skill rows
    .slice()
    .sort((a, b) => (order.get(a.skill_id) ?? 99) - (order.get(b.skill_id) ?? 99))
    .map((row) => ({
      label: skillLabelById(row.skill_id),
      cells: row.cells.map((c) => ({
        value: c.avg,
        threshold: c.bar,
        title:
          c.n > 0 && c.bar != null
            ? `avg ${c.avg?.toFixed(1)} vs expected ${c.bar.toFixed(1)} (${c.n} call${c.n === 1 ? '' : 's'})`
            : c.n > 0
              ? `avg ${c.avg?.toFixed(1)} (${c.n} call${c.n === 1 ? '' : 's'}; no minimum on record)`
              : 'no calls',
      })),
    }));
  return renderHeatmap({ columns, rows, skillHeader: 'Skill' });
}

function emptyState(): HTMLElement {
  const box = h('div', 'trends-empty');
  box.appendChild(h('h3', 'trends-empty-h', 'No calls in this view yet'));
  box.appendChild(h('p', 'trends-empty-p', 'Score a few calls of this type — or widen the window and clear the team, region and manager filters. As calls accrue, this page shows what good looks like and where the gaps are, per call type.'));
  return box;
}
